import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/shared/lib/utils";

const kpiIconVariants = cva(
  "flex size-7 shrink-0 items-center justify-center rounded-lg [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      accent: {
        primary: "bg-primary/10 text-primary dark:bg-primary/20",
        success: "bg-success/10 text-success dark:bg-success/20",
        warning: "bg-warning/10 text-warning dark:bg-warning/20",
        info: "bg-blue-500/10 text-blue-700 dark:bg-blue-400/20 dark:text-blue-300",
        danger: "bg-destructive/10 text-destructive dark:bg-destructive/20",
        neutral: "bg-muted text-muted-foreground",
      },
    },
    defaultVariants: {
      accent: "neutral",
    },
  },
);

export interface ColocaKpiCardProps
  // `onSelect` is ours; the DOM handler of the same name would collide.
  extends Omit<React.ComponentPropsWithoutRef<"div">, "onSelect">, VariantProps<typeof kpiIconVariants> {
  /** Short metric label shown next to the icon — e.g. "Total clients". */
  label: React.ReactNode;
  /** The headline figure. Rendered large with tabular numerals; pass a pre-formatted string. */
  value: React.ReactNode;
  /** Icon describing the metric, contained in a tinted rounded square. */
  icon?: LucideIcon;
  /**
   * Secondary line under the value — a delta ("▲ +4.2% vs last month"), a breakdown ("812 pending · 316 in
   * review"), or any short contextual note.
   */
  meta?: React.ReactNode;
  /**
   * Where the tinted icon sits relative to the label. `"start"` (default) keeps the established stat-strip
   * look; `"end"` pushes it to the far edge, which reads better when a row of tiles doubles as a control and
   * the labels need to align on the left.
   */
  iconPlacement?: "start" | "end";
  /**
   * Turns the tile into a toggle: the root becomes a `<button>` carrying `aria-pressed`. Pair with
   * {@link ColocaKpiCardProps.selected}. Omit it and the card stays a plain `<div>` — every existing call
   * site is unaffected.
   */
  onSelect?: () => void;
  /**
   * Pressed state of a selectable tile. Rendered as a border + ring, never as a filled surface: the `accent`
   * tints only the icon, so that a row of tiles never becomes a row of colour blocks.
   */
  selected?: boolean;
}

/**
 * Compact KPI tile for stat strips at the top of list pages and dashboards.
 *
 * Composes an icon in a tinted "squircle", a small label, a large tabular-nums value, and an optional meta
 * line (delta or breakdown). Lay several out in a responsive grid to build a bento-style stat strip.
 *
 * The `accent` only tints the icon container — the card surface stays neutral so a row of KPIs never becomes
 * a row of color blocks.
 *
 * Pass `onSelect` and the tile becomes a toggle button (`aria-pressed`), which lets a stat strip double as a
 * filter instead of restating numbers that a separate chip row already carries. Selection is drawn as a
 * border + ring so the "never a row of colour blocks" rule survives.
 *
 * @summary compact metric tile (icon + label + value + delta) for stat strips, optionally selectable
 */
// The ref type is a union because the root element depends on `onSelect`. `RefObject<T>` is invariant under
// React 19 types, so a caller holding a `useRef<HTMLDivElement>` cannot pass it here — use
// `useRef<HTMLDivElement | HTMLButtonElement>` instead.
export const ColocaKpiCard = React.forwardRef<HTMLDivElement | HTMLButtonElement, ColocaKpiCardProps>(
  function ColocaKpiCard(
    {
      label,
      value,
      icon: Icon,
      meta,
      accent,
      iconPlacement = "start",
      onSelect,
      selected = false,
      className,
      children,
      ...rest
    },
    ref,
  ) {
    const body = (
      <>
        <div
          className={cn(
            "flex items-center gap-2.5",
            iconPlacement === "end" && "flex-row-reverse justify-between",
          )}
        >
          {Icon && (
            <span className={kpiIconVariants({ accent })} aria-hidden="true">
              <Icon />
            </span>
          )}
          <span className={cn("text-xs font-medium", selected ? "text-foreground" : "text-muted-foreground")}>
            {label}
          </span>
        </div>
        <div className="text-2xl font-semibold tracking-tight text-foreground tabular-nums">{value}</div>
        {meta != null && <div className="text-xs text-muted-foreground">{meta}</div>}
        {children}
      </>
    );

    const surface = cn(
      "flex flex-col gap-2 rounded-xl border bg-card p-4",
      // Selection reads as a border + ring. Never a fill: the `accent` tints only the icon precisely so a
      // row of these never becomes a row of colour blocks.
      selected ? "border-primary/40 ring-1 ring-primary/30" : "border-border",
    );

    if (!onSelect) {
      return (
        <div ref={ref as React.Ref<HTMLDivElement>} className={cn(surface, className)} {...rest}>
          {body}
        </div>
      );
    }

    return (
      <button
        // `rest` FIRST: spreading it last let a consumer's `onClick` silently replace the select handler and
        // a consumer's `aria-pressed` override the computed state.
        {...(rest as React.ComponentPropsWithoutRef<"button">)}
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        className={cn(
          surface,
          "text-left transition-all hover:shadow-[var(--shadow-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          !selected && "hover:border-ring/40",
          className,
        )}
      >
        {body}
      </button>
    );
  },
);
